import { PdfMarkupActions, PdfMarkupActionTypes } from '@dwo/actions';
import { AnnotationsByPage } from '@app/work-order/pdf-markup/pdf-markup-annotation.model';

export interface DocumentMarkupState {
  loading: boolean;
  loaded: boolean;
  error: string | null;
  annotationsByPage: AnnotationsByPage;
}

export interface PdfMarkupState {
  byDocument: { [documentId: string]: DocumentMarkupState };
}

export const emptyDocumentState: DocumentMarkupState = {
  loading: false,
  loaded: false,
  error: null,
  annotationsByPage: {},
};

export const initialState: PdfMarkupState = {
  byDocument: {},
};

function getDocumentState(state: PdfMarkupState, documentId: string): DocumentMarkupState {
  return state.byDocument[documentId] ?? emptyDocumentState;
}

function removeAnnotation(annotationsByPage: AnnotationsByPage, markupId: string): AnnotationsByPage {
  const result: AnnotationsByPage = {};
  for (const [page, annotations] of Object.entries(annotationsByPage)) {
    result[Number(page)] = annotations.filter((a) => a.id !== markupId);
  }
  return result;
}

export function pdfMarkupReducer(state: PdfMarkupState = initialState, action: PdfMarkupActions): PdfMarkupState {
  switch (action.type) {
    case PdfMarkupActionTypes.LoadMarkups: {
      const existing = getDocumentState(state, action.documentId);
      return {
        ...state,
        byDocument: {
          ...state.byDocument,
          [action.documentId]: { ...existing, loading: true, error: null },
        },
      };
    }
    case PdfMarkupActionTypes.LoadMarkupsSuccess: {
      return {
        ...state,
        byDocument: {
          ...state.byDocument,
          [action.documentId]: {
            loading: false,
            loaded: true,
            error: null,
            annotationsByPage: action.annotationsByPage,
          },
        },
      };
    }
    case PdfMarkupActionTypes.LoadMarkupsFailure: {
      const existing = getDocumentState(state, action.documentId);
      return {
        ...state,
        byDocument: {
          ...state.byDocument,
          [action.documentId]: { ...existing, loading: false, loaded: false, error: action.error },
        },
      };
    }
    case PdfMarkupActionTypes.SaveMarkupsSuccess: {
      const existing = getDocumentState(state, action.documentId);
      return {
        ...state,
        byDocument: {
          ...state.byDocument,
          [action.documentId]: { ...existing, annotationsByPage: action.annotationsByPage, error: null },
        },
      };
    }
    case PdfMarkupActionTypes.SaveMarkupsFailure: {
      const existing = getDocumentState(state, action.documentId);
      return {
        ...state,
        byDocument: {
          ...state.byDocument,
          [action.documentId]: { ...existing, error: action.error },
        },
      };
    }
    case PdfMarkupActionTypes.DeleteMarkupSuccess: {
      const existing = getDocumentState(state, action.documentId);
      return {
        ...state,
        byDocument: {
          ...state.byDocument,
          [action.documentId]: {
            ...existing,
            annotationsByPage: removeAnnotation(existing.annotationsByPage, action.markupId),
          },
        },
      };
    }
    case PdfMarkupActionTypes.DeleteMarkupFailure: {
      const existing = getDocumentState(state, action.documentId);
      return {
        ...state,
        byDocument: {
          ...state.byDocument,
          [action.documentId]: { ...existing, error: action.error },
        },
      };
    }
    case PdfMarkupActionTypes.ClearMarkupsState: {
      const { [action.documentId]: _removed, ...rest } = state.byDocument;
      return { ...state, byDocument: rest };
    }
    default:
      return state;
  }
}
